import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { formatMoney } from '../../components/quote/TotalsPanel';
import { ROUTE, withQuery } from '../../utils/navigation';
import './business.css';

const formatWhen = (iso, locale) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatAddress = (address) => {
  if (!address || typeof address !== 'object') return '—';
  const parts = [address.street, address.city, address.state, address.zip, address.country].filter(
    Boolean,
  );
  return parts.length ? parts.join(', ') : '—';
};

const statusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'is-completed';
  if (s === 'opened' || s === 'sent') return 'is-open';
  return 'is-draft';
};

const CustomerDetailPage = ({ customerId, onNavigate, storeCurrency = 'USD' }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [savingGroup, setSavingGroup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [custRes, groupsRes] = await Promise.all([
        api.getCustomer(customerId),
        api.customerGroups(),
      ]);
      setCustomer(custRes?.customer || null);
      setGroups(groupsRes?.groups || []);
    } catch (err) {
      showToast(err.message || t('business.customerDetail.loadFailed'), 'error');
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onGroupChange = async (groupId) => {
    setSavingGroup(true);
    try {
      const res = await api.patchCustomer(customerId, {
        customerGroupId: groupId || null,
      });
      setCustomer(res?.customer || customer);
      showToast(t('business.customerDetail.groupUpdated'), 'success');
    } catch (err) {
      showToast(err.message || t('business.customerDetail.groupUpdateFailed'), 'error');
    } finally {
      setSavingGroup(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="q2c-biz">
        <p className="text-muted">{t('business.customerDetail.notFound')}</p>
        <Button size="sm" variant="outline-secondary" onClick={() => onNavigate?.(ROUTE.CUSTOMERS)}>
          {t('business.customerDetail.backToCustomers')}
        </Button>
      </div>
    );
  }

  return (
    <div className="q2c-biz">
      <div className="q2c-biz-crumb">
        <button type="button" onClick={() => onNavigate?.(ROUTE.CUSTOMERS)}>
          {t('business.customerDetail.crumbCustomers')}
        </button>
        <span>/</span>
        <span>{customer.name}</span>
      </div>

      <div className="q2c-biz-header">
        <h2>{customer.name}</h2>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() =>
            onNavigate?.(
              withQuery(ROUTE.QUOTES_CREATE, { customerId: customer.id }),
            )
          }
        >
          {t('business.customerDetail.createQuote')}
        </Button>
      </div>

      <div className="q2c-biz-detail-grid">
        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{t('business.customerDetail.customerDetails')}</div>
          <dl className="q2c-biz-dl">
            <div>
              <dt>{t('business.customerDetail.name')}</dt>
              <dd>{customer.name}</dd>
            </div>
            <div>
              <dt>{t('business.customerDetail.email')}</dt>
              <dd>{customer.email}</dd>
            </div>
            <div>
              <dt>{t('business.customerDetail.phone')}</dt>
              <dd>{customer.phone || '—'}</dd>
            </div>
            <div>
              <dt>{t('business.customerDetail.address')}</dt>
              <dd>{formatAddress(customer.address)}</dd>
            </div>
          </dl>
        </div>

        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{t('business.customerDetail.additionalInfo')}</div>
          <dl className="q2c-biz-dl">
            <div>
              <dt>{t('business.customerDetail.dateCreated')}</dt>
              <dd>{formatWhen(customer.createdAt, i18n.language)}</dd>
            </div>
            <div>
              <dt>{t('business.customerDetail.customerGroup')}</dt>
              <dd>
                <Form.Select
                  size="sm"
                  value={customer.customerGroupId || ''}
                  disabled={savingGroup}
                  onChange={(e) => onGroupChange(e.target.value)}
                >
                  <option value="">{t('business.customerDetail.selectCustomerGroup')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Form.Select>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="q2c-biz-panel mt-2">
        <div className="q2c-biz-panel-title">{t('business.customerDetail.previousQuotes')}</div>
        {(customer.previousQuotes || []).length === 0 ? (
          <div className="text-muted text-center py-3">{t('business.customerDetail.noQuotes')}</div>
        ) : (
          <div className="q2c-biz-table-wrap">
            <table className="q2c-biz-table">
              <thead>
                <tr>
                  <th>{t('business.customerDetail.colQuote')}</th>
                  <th>{t('business.customerDetail.colDateTime')}</th>
                  <th>{t('business.customerDetail.colName')}</th>
                  <th>{t('business.customerDetail.colSalesRep')}</th>
                  <th>{t('business.customerDetail.colStatus')}</th>
                  <th>{t('business.customerDetail.colTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {customer.previousQuotes.map((q) => (
                  <tr key={q.id}>
                    <td>
                      <button
                        type="button"
                        className="q2c-biz-link"
                        onClick={() => onNavigate?.(`${ROUTE.QUOTES}/${q.id}`)}
                      >
                        {q.number}
                      </button>
                    </td>
                    <td>{formatWhen(q.date, i18n.language)}</td>
                    <td>{q.name}</td>
                    <td>{q.salesRepId || '—'}</td>
                    <td>
                      <span className={`q2c-biz-status ${statusClass(q.status)}`}>{q.status ? t(`status.${q.status}`, { defaultValue: q.status }) : q.status}</span>
                    </td>
                    <td>{formatMoney(q.total, q.currency || storeCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailPage;
